import React, { useState } from 'react';
import { reverseGeocode } from './googleMapsTravel.js';

// ─── One-time consent ─────────────────────────────────────
// The browser's own permission prompt is the hard gate, but it gives the user
// no idea *why* a page wants their location. We show a plain-language notice
// once, before triggering that prompt, and remember the answer per device —
// the same granularity the browser permission itself uses.
const CONSENT_KEY = 'bvm.location.consent.v1';

export const hasLocationConsent = () => {
  try {
    return JSON.parse(window.localStorage.getItem(CONSENT_KEY))?.granted === true;
  } catch {
    return false;
  }
};

export const setLocationConsent = (granted) => {
  try {
    if (granted) {
      window.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ granted: true, at: new Date().toISOString() }),
      );
    } else {
      window.localStorage.removeItem(CONSENT_KEY);
    }
  } catch {
    // Private mode / storage disabled — the notice simply shows every time.
  }
};

// Shown in place of the crosshair while the device fix + reverse geocode run.
const SpinnerIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" className="animate-spin" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.6" opacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

const PinIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2v3.2M12 18.8V22M22 12h-3.2M5.2 12H2" />
    <circle cx="12" cy="12" r="8.2" opacity="0.55" />
  </svg>
);

/**
 * Small crosshair button that fills an address field with the device's current
 * location. Designed to sit overlaid inside the left edge of a text input.
 *
 * Props:
 *   onResolved(address) — called with a formatted address (or "lat, lng")
 *   align — which side the consent/error popover opens toward
 */
const CurrentLocationButton = ({ onResolved, align = 'left', compact = false, bounce = false }) => {
  const [phase, setPhase] = useState('idle'); // idle | consent | locating | error
  const [error, setError] = useState(null);

  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  if (!supported) return null;

  const locate = () => {
    setPhase('locating');
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Raw coordinates are a valid Maps destination, so they are a safe
        // fallback when reverse geocoding is unavailable.
        let address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        try {
          const resolved = await reverseGeocode(latitude, longitude);
          if (resolved) address = resolved;
        } catch (err) {
          console.warn('[Location] Reverse geocoding failed, using coordinates:', err.message);
        }
        setPhase('idle');
        onResolved(address);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          // A browser-level block outlives our stored answer, so clear it —
          // otherwise we'd silently retry a prompt that can never appear.
          setLocationConsent(false);
          setError('Location is blocked for this site. Allow it in your browser’s site settings, then try again.');
        } else if (err.code === err.TIMEOUT) {
          setError('Timed out getting a fix. Try again with a clearer view of the sky.');
        } else {
          setError('Could not get your location right now.');
        }
        setPhase('error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const handleClick = () => {
    if (phase === 'locating') return;
    if (phase === 'consent' || phase === 'error') { setPhase('idle'); return; }
    if (hasLocationConsent()) locate();
    else setPhase('consent');
  };

  const popoverSide = align === 'right' ? 'right-0' : 'left-0';

  return (
    <span className={`relative inline-flex ${bounce && phase === 'idle' ? 'animate-tour-zoom' : ''}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={phase === 'locating'}
        title="Use my current location"
        aria-label="Use my current location"
        className={`inline-flex items-center justify-center rounded transition-all duration-150 ${
          compact ? 'w-5 h-5' : 'w-6 h-6'
        } ${
          phase === 'locating'
            ? 'text-green-400'
            : phase === 'error'
              ? 'text-red-300 hover:text-red-200 hover:scale-125'
              : 'text-gray-400 hover:text-green-400 hover:bg-green-600/10 hover:scale-125'
        }`}
      >
        {phase === 'locating' ? <SpinnerIcon /> : <PinIcon />}
      </button>

      {(phase === 'consent' || phase === 'error') && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setPhase('idle')} />
          {/* Solid gray-900 rather than the gray-800 panel tone: this floats over
              gray-700 cards, so it needs to read as a layer above them. */}
          <div className={`absolute top-full mt-1.5 ${popoverSide} z-40 w-80 bg-gray-900 border border-gray-500/60 rounded-lg shadow-2xl p-4 text-left`}>
            {phase === 'consent' ? (
              <>
                <p className="text-sm font-semibold text-white mb-1.5">Use your device location?</p>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Your device location is read only when you tap this button, and only
                  to fill in this address field. It is not tracked in the background
                  and is not stored anywhere unless you save the address. You can
                  change this any time in your browser&rsquo;s site settings.
                </p>
                <div className="flex justify-end gap-2 mt-3.5">
                  <button
                    type="button"
                    onClick={() => setPhase('idle')}
                    className="px-3 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-500/20 rounded transition-colors"
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLocationConsent(true); locate(); }}
                    className="px-3.5 py-1.5 text-xs font-medium bg-green-700 text-white rounded hover:bg-green-900 transition-colors"
                  >
                    Use my location
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-red-200 mb-1.5">Location unavailable</p>
                <p className="text-xs text-gray-300 leading-relaxed">{error}</p>
                <div className="flex justify-end mt-3.5">
                  <button
                    type="button"
                    onClick={() => setPhase('idle')}
                    className="px-3 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-500/20 rounded transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </span>
  );
};

export default CurrentLocationButton;
